import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="copy-article-content"
export default class extends Controller {
  static targets = [ "preview", "textLabel", "htmlLabel" ]
  static values = {
    copiedLabel: { type: String, default: "コピーしました" },
    resetDelay: { type: Number, default: 2000 },
  }

  declare readonly hasPreviewTarget: boolean
  declare readonly previewTarget: HTMLElement
  declare readonly hasTextLabelTarget: boolean
  declare readonly textLabelTarget: HTMLElement
  declare readonly hasHtmlLabelTarget: boolean
  declare readonly htmlLabelTarget: HTMLElement
  declare copiedLabelValue: string
  declare resetDelayValue: number

  private labelTimers: Map<HTMLElement, number> = new Map()

  connect(): void {
    if (this.hasTextLabelTarget) this.storeOriginalLabel(this.textLabelTarget)
    if (this.hasHtmlLabelTarget) this.storeOriginalLabel(this.htmlLabelTarget)
  }

  copyText(event: Event): void {
    event.preventDefault()
    if (!this.hasPreviewTarget) return

    const content = this.previewTarget.innerText.trim()
    this.copyContent(content, this.hasTextLabelTarget ? this.textLabelTarget : undefined)
  }

  copyHtml(event: Event): void {
    event.preventDefault()
    if (!this.hasPreviewTarget) return

    const content = this.previewTarget.innerHTML.trim()
    this.copyContent(content, this.hasHtmlLabelTarget ? this.htmlLabelTarget : undefined)
  }

  private async copyContent(content: string, label?: HTMLElement): Promise<void> {
    if (!content) return

    try {
      await navigator.clipboard.writeText(content)
    } catch (error) {
      this.copyWithFallback(content)
    } finally {
      if (label) this.showCopiedState(label)
    }
  }

  private copyWithFallback(content: string): void {
    const textarea = document.createElement('textarea')
    textarea.value = content
    textarea.style.position = 'fixed'
    textarea.style.top = '-1000px'
    textarea.style.opacity = '0'
    textarea.setAttribute('readonly', 'true')
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand('copy')
    } catch (error) {
      console.error('Failed to copy text: ', error)
    } finally {
      document.body.removeChild(textarea)
    }
  }

  private showCopiedState(label: HTMLElement): void {
    const original = label.dataset.originalLabel
    if (!original) return

    label.textContent = this.copiedLabelValue

    const timerId = this.labelTimers.get(label)
    if (timerId) window.clearTimeout(timerId)

    const resetId = window.setTimeout(() => {
      label.textContent = original
      this.labelTimers.delete(label)
    }, this.resetDelayValue)

    this.labelTimers.set(label, resetId)
  }

  private storeOriginalLabel(label: HTMLElement): void {
    if (!label.dataset.originalLabel) {
      label.dataset.originalLabel = label.textContent?.trim() || ''
    }
  }
}
